import { useEffect, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@rocicorp/zero/react'
import { useAuth } from '../auth/AuthContext.tsx'
import { AddFeeForm } from '../components/AddFeeForm.tsx'
import { CollapsibleSection } from '../components/CollapsibleSection.tsx'
import { Shell } from '../components/Shell.tsx'
import { UserTab } from '../components/UserTab.tsx'
import { formatTime } from '../lib/format.ts'
import { queries } from '../zero/queries.ts'
import { Require } from './guards.tsx'

interface PublicDrink {
  id: string
  purchasedAt: number
  drinkName: string
  guestName: string | null
}

interface PublicProfile {
  id: string
  displayName: string
  drinks: PublicDrink[]
}

function UserDetailInner() {
  const { userId } = useParams({ from: '/users/$userId' })
  const navigate = useNavigate()
  const { session } = useAuth()
  const [member] = useQuery(queries.userById({ userId }))
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [error, setError] = useState('')

  const canSeeFinances =
    !!session && (session.auth.role === 'admin' || session.auth.sub === userId)

  // US-09's sanitized projection, fetched only for viewers who cannot see the
  // tab itself. The owner and admins get the complete Zero-backed history from
  // UserTab instead, so nobody is shown two overlapping drink lists.
  useEffect(() => {
    if (!session || canSeeFinances) return
    const controller = new AbortController()
    setProfile(null)
    setError('')
    void fetch(`/api/users/${encodeURIComponent(userId)}/public-profile`, {
      headers: { authorization: `Bearer ${session.token}` },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Unable to load member')
        setProfile((await res.json()) as PublicProfile)
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setError('Unable to load member')
      })
    return () => controller.abort()
  }, [canSeeFinances, session, userId])

  if (!session) return null

  return (
    <Shell
      title={member?.displayName ?? profile?.displayName ?? 'Member'}
      onBack={() => navigate({ to: '/' })}
    >
      {canSeeFinances ? (
        <>
          <UserTab userId={userId} />
          {session.auth.role === 'admin' ? <AddFeeForm userId={userId} /> : null}
        </>
      ) : (
        <>
          {error ? <p className="error">{error}</p> : null}
          {!profile && !error ? <p className="muted center">Loading member…</p> : null}
          {profile ? (
            <>
              <CollapsibleSection title="Drink history" count={profile.drinks.length}>
                <div className="card">
                  {profile.drinks.length === 0 ? (
                    <p className="empty">No drinks recorded.</p>
                  ) : (
                    profile.drinks.map((drink) => (
                      <div className="line" key={drink.id}>
                        <div className="line-main">
                          <div className="line-title">
                            {drink.drinkName}
                            {drink.guestName ? (
                              <span className="muted"> · for {drink.guestName}</span>
                            ) : null}
                          </div>
                          <div className="line-sub">{formatTime(drink.purchasedAt)}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CollapsibleSection>
            </>
          ) : null}
        </>
      )}
    </Shell>
  )
}

export function UserDetailPage() {
  return (
    <Require roles={['member', 'admin', 'kiosk']}>
      <UserDetailInner />
    </Require>
  )
}
